// RsfChart.jsx
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import "../BenFordStyle.css";

/**
 * Props:
 *  - data: array of points (each point should have .row containing backend fields)
 *  - dep: dependent variable name (string)
 *  - groupBy: array of group-by column names
 */
const RsfChart = ({ data = [], dep, groupBy = [] }) => {
  const chartRef = useRef(null);
  const instanceRef = useRef(null);

  const extractY = (rowObj, pt) => {
    const candidates = [
      "RSF_On_Max",
      "RSF_ON_MAX",
      "RSF_ON_MAX_VALUE",
      "RSF",
      "RSF_SCORE",
      "RSF_SCORE_VAL",
      dep,
    ];
    for (const c of candidates) {
      if (!c) continue;
      const val =
        (rowObj &&
          (rowObj[c] ?? rowObj[c.toLowerCase()] ?? rowObj[c.toUpperCase()])) ??
        (pt && (pt[c] ?? pt[c.toLowerCase()]));
      if (val !== undefined && val !== null && val !== "") {
        const cleaned = String(val).replace(/,/g, "").trim();
        const parsed = parseFloat(cleaned);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    if (typeof pt?.y === "number" && Number.isFinite(pt.y)) return pt.y;
    if (typeof pt?.y === "string") {
      const p = parseFloat(pt.y.replace(/,/g, ""));
      if (!Number.isNaN(p)) return p;
    }
    return null;
  };

  const prepare = (raw) => {
    const useGroup = Array.isArray(groupBy) && groupBy.length > 0;
    const groups = [];
    const seriesData = [];

    if (useGroup) {
      const set = new Set();
      raw.forEach((pt) => {
        const row = pt.row ?? pt;
        const keyParts = groupBy.map((g) =>
          row && row[g] !== undefined ? String(row[g]) : "N/A"
        );
        set.add(keyParts.join(" | "));
      });
      groups.push(...Array.from(set));
      const map = {};
      groups.forEach((g) => (map[g] = { sum: 0, count: 0 }));
      raw.forEach((pt) => {
        const row = pt.row ?? pt;
        const keyParts = groupBy.map((g) =>
          row && row[g] !== undefined ? String(row[g]) : "N/A"
        );
        const gk = keyParts.join(" | ");
        const y = extractY(row, pt);
        if (y !== null) {
          map[gk].sum += y;
          map[gk].count += 1;
        }
      });
      groups.forEach((g) => {
        const entry = map[g];
        const val = entry.count > 0 ? entry.sum / entry.count : 0;
        seriesData.push({ name: g, value: val });
      });
      return { xType: "category", categories: groups, seriesData };
    } else {
      raw.forEach((pt, idx) => {
        const row = pt.row ?? pt;
        const label = row?.Vendor_Name ?? row?.Vendor ?? `Row ${idx + 1}`;
        const y = extractY(row, pt);
        seriesData.push({ name: label, value: y !== null ? y : 0 });
      });
      return {
        xType: "category",
        categories: seriesData.map((s) => s.name),
        seriesData,
      };
    }
  };

  // helper: truncate label but keep full in tooltip
  const makeLabelTruncator = (maxChars = 12) => (value) => {
    if (!value) return "";
    if (value.length <= maxChars) return value;
    return value.slice(0, maxChars - 1) + "…";
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const prepared = prepare(data || []);
    const categoryCount = (prepared.categories || []).length;

    // dynamic default view window: if too many categories, show only a portion initially
    let defaultStart = 0,
      defaultEnd = 100;
    if (categoryCount > 40) {
      defaultEnd = 25; // show first 25%
    } else if (categoryCount > 20) {
      defaultEnd = 40; // show first 40%
    }

    const labelTruncate = makeLabelTruncator(15); // adjust chars as needed

    const option = {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: function (params) {
          if (!params || !params.length) return "";
          const p = params[0];
          const fullName = prepared.categories?.[p.dataIndex] ?? p.name;
          return `
    <div style="font-size:13px;line-height:1.5;">
      <strong>${fullName}</strong><br/>
      ${dep || "Value"}: <b>${p.value}</b>
    </div>
  `;
        },
      },

      // give more bottom space for rotated labels and slider
      grid: { left: 80, right: 30, bottom: 120, top: 60 },

      xAxis: {
        type: prepared.xType,
        data: prepared.categories,
        axisLabel: {
          rotate: 45,
          interval: 0, // show all (we truncate text). Change to >0 to show every nth label.
          margin: 14,
          color: "#000",
          fontSize: 11,
          fontWeight: "bold",
          formatter: labelTruncate,
        },
        name: groupBy && groupBy.length ? groupBy.join(" | ") : "Vendor_Name",
      },
      yAxis: {
        type: "value",
        name: dep || "Value",
        axisLabel: { fontSize: 13, fontWeight: "bold", color: "#000" },
      },

      dataZoom: [
        {
          type: "slider",
          show: true,
          xAxisIndex: [0],
          start: defaultStart,
          end: defaultEnd,
          height: 18,
          bottom: 18, // moves slider visually below labels (grid.bottom leaves more room)
          fillerColor: "rgba(0,0,0,0.05)",
          handleSize: "100%",
          handleIcon:
            "M8.2,13.6c-0.2,0.2-0.5,0.2-0.7,0L0.7,6.8C0.5,6.6,0.5,6.3,0.7,6.1l1.1-1.1c0.2-0.2,0.5-0.2,0.7,0L8,10.4l5.5-5.4  c0.2-0.2,0.5-0.2,0.7,0l1.1,1.1c0.2,0.2,0.2,0.5,0,0.7L8.2,13.6z",
        },
        {
          type: "inside",
          xAxisIndex: [0],
          start: defaultStart,
          end: defaultEnd,
        },
      ],

      series: [
        {
          type: "bar",
          data: prepared.seriesData.map((s) => s.value),
          name: dep || "RSF Value",
          barWidth: Math.max(6, Math.floor(600 / Math.max(1, categoryCount))), // make bars narrower if many categories
          itemStyle: {
            color: "#FFA500",
            opacity: 0.95,
          },
          emphasis: {
            focus: "series",
            itemStyle: {
              color: "#FF8C00",
            },
          },
        },
      ],
    };

    if (!instanceRef.current)
      instanceRef.current = echarts.init(chartRef.current, null, {
        // increase DPR display fidelity if needed
      });

    instanceRef.current.setOption(option, true);

    // If many categories, programmatically call resize once to help echarts layout
    setTimeout(() => instanceRef.current && instanceRef.current.resize(), 50);

    const handleResize = () =>
      instanceRef.current && instanceRef.current.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data, dep, groupBy]);

  useEffect(() => {
    return () => {
      if (instanceRef.current) {
        try {
          instanceRef.current.dispose();
        } catch (e) {}
        instanceRef.current = null;
      }
    };
  }, []);

  // container style: give explicit height so chart has room
  return (
    <div
      className="one-wrapper-BenFord"
      ref={chartRef}
      style={{ width: "100%", height: "100%" }} // adjust height to your layout
    />
  );
};

export default RsfChart;
